// "What Neo knows about this boat", on the Equipment page (TOM-610, cloud ADR
// 0370, ADR 0373): the boat's notebook, by topic, each note filed under its
// piece of equipment when the page shows one. Every owner of the boat shares
// it and may add a note in their own words, confirm a note Neo noticed in the
// readings, correct a note, or forget one. Undo takes back the owner's own
// last add, confirm or correct; a forget is final, so it asks first.
//
// Neo speaks with one voice: no expert is ever named here.
//
// An owner can also ask Neo to study a part of the boat on its own (TOM-615,
// cloud ADR 0379): "Have Neo study the boat's batteries and charging". The
// study runs in the background inside a budget; while it runs the page says
// so and looks again every few seconds, and when it ends the notes are read
// again. A press the budget turns down is said plainly.

import { useDemoGuest } from '../../auth';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import {
  BOAT_NOTE_TEXT_MAX,
  NOTE_KIND_CHOICES,
  NOTE_TOPICS,
  addBoatNoteOnPage,
  canConfirm,
  changeBoatNote,
  equipmentChoices,
  noteConfirmation,
  noteRefusalText,
  noteSource,
  noteStateText,
  notebookSections,
  type BoatNote,
  type BoatNoteAction,
  type BoatNoteArea,
  type BoatNoteKind,
} from '../../lib/boatNotes';
import { useBoatOwners } from '../../lib/boatOwners';
import {
  askBoatStudy,
  fetchBoatStudy,
  studyButtonLabel,
  studyRefusalText,
  studyEndedText,
  studyRunningText,
  studiedText,
  type BoatStudyArea,
} from '../../lib/boatStudy';
import type { EquipmentEntry } from '../../lib/equipment';
import { useBoatNotebook } from '../../lib/useBoatNotebook';
import { ChevronRightIcon } from '../app/icons';

const CHANGE_FAILED =
  'Couldn’t change that note. Nothing changed. Check the connection and try again.';
const CHANGE_CONFLICT =
  'This note changed since you opened the page, so nothing was saved. It shows the latest now.';
const EDIT_CONFLICT =
  'This note changed since you opened the page, so your words weren’t saved yet. They’re still here: save again to replace what it says now.';
const ADD_FAILED = 'Couldn’t add that note. Check the connection and try again.';
const FORGOTTEN = 'Forgotten. Neo won’t use that note, or learn it again from the readings.';
const GONE = 'That note is gone. Another owner may have forgotten it.';
const ADD_UNDONE = 'Undone. The note you added is gone.';

/** The owner's last add, confirm or correct, which Undo takes back, or the undo just done. */
type Recent =
  | { id: string; action: 'add' | 'confirm' | 'correct'; version: number }
  | { id: string; action: 'undone' };

/** A message on one note; `latest` shows what the note says now under an editor kept open. */
interface NoteError {
  id: string;
  text: string;
  latest?: boolean;
}

/** A note just forgotten, or found gone, held in its place for one line. */
interface Gone {
  note: BoatNote;
  /** Where it stood in the notebook, so the line stays where the note was. */
  index: number;
  message: string;
}

const DONE_TEXT: Readonly<Record<Recent['action'], string>> = {
  add: 'Added. Everyone on the boat sees it.',
  confirm: 'Confirmed. Neo will treat it as right.',
  correct: 'Saved in your words.',
  undone: 'Undone. The note is as it was.',
};

/** The line a forgotten or vanished note leaves in its place: no words, no actions. */
function GoneRow({ message }: { message: string }) {
  return (
    <li className="notebook-note" data-testid="notebook-gone">
      <p className="notebook-note__done" role="status">
        {message}
      </p>
    </li>
  );
}

interface NoteRowProps {
  note: BoatNote;
  owners: Parameters<typeof noteSource>[1];
  busy: boolean;
  editing: string | null;
  armedForget: boolean;
  recent: Recent | null;
  error: NoteError | null;
  onAction: (note: BoatNote, action: BoatNoteAction, text?: string) => void;
  onEdit: (text: string | null) => void;
  onArmForget: (armed: boolean) => void;
}

function NoteRow({
  note,
  owners,
  busy,
  editing,
  armedForget,
  recent,
  error,
  onAction,
  onEdit,
  onArmForget,
}: NoteRowProps) {
  const confirmation = noteConfirmation(note, owners);
  const state = noteStateText(note);
  const confirmed = note.origin === 'observed' && note.confirmedAt !== null && state === null;
  const editId = `notebook-edit-${note.id}`;

  function onSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (editing !== null) onAction(note, 'correct', editing);
  }

  return (
    <li className="notebook-note" data-testid="notebook-note" data-note-id={note.id}>
      <div className="notebook-note__body">
        {editing === null ? (
          <p className="notebook-note__text">{note.text}</p>
        ) : (
          <form className="notebook-note__edit" onSubmit={onSave}>
            <label className="sr-only" htmlFor={editId}>
              Correct the note
            </label>
            <textarea
              id={editId}
              className="form-textarea notebook-note__input"
              value={editing}
              maxLength={BOAT_NOTE_TEXT_MAX}
              rows={3}
              data-testid="notebook-edit-text"
              onChange={(e) => onEdit(e.target.value)}
            />
            <div className="notebook-note__edit-row">
              <span className="notebook-note__count">
                {editing.length}/{BOAT_NOTE_TEXT_MAX}
              </span>
              <button
                type="button"
                className="tb-btn tb-btn--sm tb-btn--plain"
                disabled={busy}
                onClick={() => onEdit(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="tb-btn tb-btn--sm tb-btn--tinted"
                disabled={busy || editing.trim().length === 0}
                data-testid="notebook-save"
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
        <p className="notebook-note__meta">
          {noteSource(note, owners)}
          {confirmation !== null ? ` · ${confirmation}` : ''}
          {confirmed ? (
            <span className="tb-chip tb-chip--good notebook-note__chip">Confirmed</span>
          ) : null}
        </p>
        {state !== null ? (
          <p className="notebook-note__state" data-testid="notebook-note-state">
            {state}
          </p>
        ) : null}
        {armedForget ? (
          <div className="notebook-note__forget" data-testid="notebook-forget-ask">
            <p>
              Forget this note? Neo stops using it and won’t learn it again from the readings. This
              can’t be undone.
            </p>
            <div className="notebook-note__buttons">
              <button
                type="button"
                className="tb-btn tb-btn--sm tb-btn--destructive"
                disabled={busy}
                data-testid="notebook-forget-confirm"
                onClick={() => onAction(note, 'forget')}
              >
                {busy ? 'Forgetting…' : 'Forget'}
              </button>
              <button
                type="button"
                className="tb-btn tb-btn--sm tb-btn--gray"
                disabled={busy}
                onClick={() => onArmForget(false)}
              >
                Keep
              </button>
            </div>
          </div>
        ) : null}
        {recent !== null && editing === null && !armedForget ? (
          <p className="notebook-note__done" role="status" data-testid="notebook-done">
            {DONE_TEXT[recent.action]}
            {recent.action !== 'undone' ? (
              <button
                type="button"
                className="tb-btn tb-btn--sm tb-btn--plain notebook-note__undo"
                disabled={busy}
                data-testid="notebook-undo"
                onClick={() => onAction(note, 'undo')}
              >
                Undo
              </button>
            ) : null}
          </p>
        ) : null}
        {error !== null ? (
          <div role="alert" data-testid="notebook-note-error">
            <p className="alarm-form-err">{error.text}</p>
            {error.latest === true && editing !== null ? (
              <p className="notebook-note__latest" data-testid="notebook-note-latest">
                It now says: “{note.text}”
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      {editing === null && !armedForget ? (
        <div className="notebook-note__actions" role="group" aria-label="Change this note">
          {canConfirm(note) ? (
            <button
              type="button"
              className="tb-btn tb-btn--sm tb-btn--gray"
              disabled={busy}
              data-testid="notebook-confirm"
              onClick={() => onAction(note, 'confirm')}
            >
              Confirm
            </button>
          ) : null}
          <button
            type="button"
            className="tb-btn tb-btn--sm tb-btn--gray"
            disabled={busy}
            data-testid="notebook-correct"
            onClick={() => onEdit(note.text)}
          >
            Correct
          </button>
          <button
            type="button"
            className="tb-btn tb-btn--sm tb-btn--plain"
            disabled={busy}
            data-testid="notebook-forget"
            onClick={() => onArmForget(true)}
          >
            Forget
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * "Add a note": a topic, what in it the note is about (a piece of equipment
 * the page shows, or the topic as a whole), the sort of fact, and the words.
 */
function AddNoteForm({
  equipment,
  busy,
  error,
  onSave,
  onCancel,
}: {
  equipment: readonly EquipmentEntry[];
  busy: boolean;
  error: string | null;
  onSave: (input: {
    area: BoatNoteArea;
    subject: string | null;
    kind: BoatNoteKind;
    text: string;
  }) => void;
  onCancel: () => void;
}) {
  const [area, setArea] = useState<BoatNoteArea>('electrical');
  const [subject, setSubject] = useState<string>('');
  const [kind, setKind] = useState<BoatNoteKind>('identity');
  const [text, setText] = useState('');
  const topic = NOTE_TOPICS.find((t) => t.area === area)!;
  const choices = equipmentChoices(area, equipment);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSave({ area, subject: subject === '' ? null : subject, kind, text });
  }

  return (
    <form className="notebook-add__form" onSubmit={onSubmit} data-testid="notebook-add-form">
      <div className="notebook-add__fields">
        <label className="tb-field">
          <span className="tb-field__label">Topic</span>
          <select
            className="tb-select"
            value={area}
            data-testid="notebook-add-topic"
            onChange={(e) => {
              setArea(e.target.value as BoatNoteArea);
              setSubject('');
            }}
          >
            {NOTE_TOPICS.map((t) => (
              <option key={t.area} value={t.area}>
                {t.heading}
              </option>
            ))}
          </select>
        </label>
        {choices.length > 0 ? (
          <label className="tb-field">
            <span className="tb-field__label">About</span>
            <select
              className="tb-select"
              value={subject}
              data-testid="notebook-add-about"
              onChange={(e) => setSubject(e.target.value)}
            >
              <option value="">{topic.heading} in general</option>
              {choices.map((c) => (
                <option key={c.instance} value={c.instance}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="tb-field">
          <span className="tb-field__label">What sort of fact</span>
          <select
            className="tb-select"
            value={kind}
            data-testid="notebook-add-kind"
            onChange={(e) => setKind(e.target.value as BoatNoteKind)}
          >
            {NOTE_KIND_CHOICES.map((k) => (
              <option key={k.kind} value={k.kind}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="tb-field">
        <span className="tb-field__label">The note</span>
        <textarea
          className="tb-textarea notebook-note__input"
          value={text}
          maxLength={BOAT_NOTE_TEXT_MAX}
          rows={3}
          placeholder="Something that stays true of the boat, such as how a battery bank is wired."
          data-testid="notebook-add-text"
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="notebook-note__edit-row">
        <span className="notebook-note__count">
          {text.length}/{BOAT_NOTE_TEXT_MAX}
        </span>
        <button
          type="button"
          className="tb-btn tb-btn--sm tb-btn--plain"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="tb-btn tb-btn--sm tb-btn--tinted"
          disabled={busy || text.trim().length === 0}
          data-testid="notebook-add-save"
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
      {error !== null ? (
        <p className="alarm-form-err" role="alert" data-testid="notebook-add-error">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/** How often the page looks again while a study runs. */
const STUDY_POLL_MS = 8_000;

/**
 * "Have Neo study the boat's …", one button per part of the boat Neo studies
 * on its own (TOM-615). Calls `onDone` when a study it saw running has ended.
 */
function NotebookStudy({ boatId, onDone }: { boatId: string; onDone: () => void }) {
  // A Try it guest cannot start a study, which calls a model (TOM-868).
  const guest = useDemoGuest();
  const [areas, setAreas] = useState<BoatStudyArea[] | null>(null);
  const [busy, setBusy] = useState<BoatStudyArea['area'] | null>(null);
  const [message, setMessage] = useState<{
    area: BoatStudyArea['area'];
    text: string;
    error: boolean;
  } | null>(null);
  // The areas seen running, so the page says when each one ends.
  const runningRef = useRef<Set<string>>(new Set());

  const load = useCallback(() => {
    // The study buttons are hidden for a guest, so their list is not read.
    if (guest) return;
    void fetchBoatStudy(boatId).then(
      (next) => {
        const ended = next.filter((a) => !a.running && runningRef.current.has(a.area));
        runningRef.current = new Set(next.filter((a) => a.running).map((a) => a.area));
        setAreas(next);
        if (ended.length > 0) {
          const { area, lastStatus } = ended[0]!;
          setMessage({
            area,
            text: studyEndedText(area, lastStatus),
            error: lastStatus !== 'succeeded',
          });
          onDone();
        }
      },
      // Without the list the buttons stay hidden: the notebook still works.
      () => undefined
    );
  }, [boatId, onDone, guest]);

  useEffect(() => {
    runningRef.current = new Set();
    setAreas(null);
    setMessage(null);
    load();
  }, [load]);

  const anyRunning = areas?.some((a) => a.running) ?? false;
  useEffect(() => {
    if (!anyRunning) return;
    const timer = setTimeout(load, STUDY_POLL_MS);
    return () => clearTimeout(timer);
  }, [anyRunning, areas, load]);

  function markRunning(area: BoatStudyArea['area']) {
    runningRef.current.add(area);
    setAreas((prev) => prev?.map((a) => (a.area === area ? { ...a, running: true } : a)) ?? prev);
  }

  async function onPress(area: BoatStudyArea['area']) {
    setBusy(area);
    setMessage(null);
    const press = await askBoatStudy(boatId, area, crypto.randomUUID());
    setBusy(null);
    if (press.kind === 'started') {
      markRunning(area);
      return;
    }
    if (press.kind === 'refused' && press.code === 'study_running') markRunning(area);
    setMessage({ area, text: studyRefusalText(area, press), error: true });
  }

  if (guest || areas === null || areas.length === 0) return null;
  return (
    <div className="notebook-study" data-testid="notebook-study">
      {areas.map((a) => (
        <div key={a.area} className="notebook-study__row" data-testid={`notebook-study-${a.area}`}>
          {a.running ? (
            <p
              className="notebook-study__running"
              role="status"
              data-testid="notebook-study-running"
            >
              {studyRunningText(a.area)}
            </p>
          ) : (
            <button
              type="button"
              className="tb-btn tb-btn--sm tb-btn--gray"
              disabled={busy !== null}
              data-testid={`notebook-study-button-${a.area}`}
              onClick={() => void onPress(a.area)}
            >
              {busy === a.area ? 'Asking Neo…' : studyButtonLabel(a.area)}
            </button>
          )}
          {!a.running && a.lastStudiedAt !== null ? (
            <p className="notebook-study__last" data-testid="notebook-study-last">
              {studiedText(a.lastStudiedAt)}
            </p>
          ) : null}
          {message !== null && message.area === a.area ? (
            <p
              className={message.error ? 'notebook-study__refused' : 'notebook-study__done'}
              role={message.error ? 'alert' : 'status'}
              data-testid="notebook-study-message"
            >
              {message.text}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function BoatNotebook({
  boatId,
  boatName,
  equipment,
}: {
  boatId: string;
  boatName: string;
  equipment: readonly EquipmentEntry[];
}) {
  const { load, refresh, apply, drop } = useBoatNotebook(boatId);
  // A Try it guest is refused the owners list (TOM-868): notes then name no owner.
  const guest = useDemoGuest();
  const { state: ownersState } = useBoatOwners(guest ? null : boatId);
  const owners = ownersState.kind === 'ready' ? ownersState.owners : null;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [armedForget, setArmedForget] = useState<string | null>(null);
  const [recent, setRecent] = useState<Recent | null>(null);
  const [noteError, setNoteError] = useState<NoteError | null>(null);
  const [gone, setGone] = useState<Gone | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const notes = load.kind === 'ready' ? load.notes : [];

  async function onAdd(input: {
    area: BoatNoteArea;
    subject: string | null;
    kind: BoatNoteKind;
    text: string;
  }) {
    setBusyId('new');
    setAddError(null);
    setNoteError(null);
    setGone(null);
    const result = await addBoatNoteOnPage(boatId, input, crypto.randomUUID());
    setBusyId(null);
    if (result.kind === 'saved' && result.note !== null) {
      apply(result.note);
      setAdding(false);
      setRecent({ id: result.note.id, action: 'add', version: result.note.version });
    } else {
      setAddError(result.kind === 'refused' ? noteRefusalText(result.code) : ADD_FAILED);
    }
    // An owner's note can set another aside: read them all.
    refresh();
  }

  async function onAction(note: BoatNote, action: BoatNoteAction, text?: string) {
    // Undo names the version the owner's own change left.
    const version =
      action === 'undo' && recent?.action !== 'undone' && recent?.id === note.id
        ? recent.version
        : note.version;
    setBusyId(note.id);
    setNoteError(null);
    setGone(null);
    const index = notes.findIndex((n) => n.id === note.id);
    const result = await changeBoatNote(
      boatId,
      { id: note.id, version },
      action,
      crypto.randomUUID(),
      text
    );
    setBusyId(null);
    if (result.kind === 'saved') {
      if (result.note === null) {
        // A forget, or the undo of an add: the note leaves the page.
        drop(note.id);
        setArmedForget(null);
        setRecent(null);
        setGone({ note, index, message: action === 'forget' ? FORGOTTEN : ADD_UNDONE });
      } else {
        apply(result.note);
        setEditing(null);
        setRecent(
          action === 'undo'
            ? { id: note.id, action: 'undone' }
            : { id: note.id, action: action as 'confirm' | 'correct', version: result.note.version }
        );
      }
    } else if (result.kind === 'conflict') {
      // Said on the note itself, which the read below shows as it is now. A
      // correction keeps the owner's words in the editor, to save again.
      setArmedForget(null);
      setRecent(null);
      if (action === 'correct') {
        setNoteError({ id: note.id, text: EDIT_CONFLICT, latest: true });
      } else {
        setEditing(null);
        setNoteError({ id: note.id, text: CHANGE_CONFLICT });
      }
    } else if (result.kind === 'refused' && result.code === 'note_not_found') {
      drop(note.id);
      setEditing(null);
      setArmedForget(null);
      setRecent(null);
      setGone({ note, index, message: GONE });
    } else {
      if (action === 'undo') setRecent(null);
      setNoteError({
        id: note.id,
        text: result.kind === 'refused' ? noteRefusalText(result.code) : CHANGE_FAILED,
      });
    }
    // A change can set another note aside or bring one back: read them all.
    refresh();
  }

  // A note just forgotten keeps its place for one line, until the next change.
  const shown =
    gone === null || notes.some((n) => n.id === gone.note.id)
      ? notes
      : [...notes.slice(0, gone.index), gone.note, ...notes.slice(gone.index)];
  const { topics, outOfDate } = notebookSections(shown, equipment);

  function row(note: BoatNote) {
    if (gone !== null && gone.note.id === note.id) {
      return <GoneRow key={note.id} message={gone.message} />;
    }
    return (
      <NoteRow
        key={note.id}
        note={note}
        owners={owners}
        busy={busyId !== null}
        editing={editing?.id === note.id ? editing.text : null}
        armedForget={armedForget === note.id}
        recent={recent?.id === note.id ? recent : null}
        error={noteError?.id === note.id ? noteError : null}
        onAction={(n, a, t) => void onAction(n, a, t)}
        onEdit={(t) => {
          setNoteError(null);
          setArmedForget(null);
          setEditing(t === null ? null : { id: note.id, text: t });
        }}
        onArmForget={(armed) => {
          setNoteError(null);
          setEditing(null);
          setArmedForget(armed ? note.id : null);
        }}
      />
    );
  }

  return (
    <section
      className="notebook-section"
      aria-labelledby="notebook-title"
      data-testid="boat-notebook"
    >
      <div className="notebook-section__head">
        <div>
          <h2 id="notebook-title" className="notebook-section__title">
            What Neo knows about this boat
          </h2>
          <p className="notebook-section__sub" data-testid="notebook-shared">
            Neo uses these notes when it answers about {boatName}. They’re shared with everyone who
            owns the boat, and any of you can add, confirm, correct or forget them.
          </p>
        </div>
        {!adding && load.kind === 'ready' ? (
          <button
            type="button"
            className="tb-btn tb-btn--sm tb-btn--tinted"
            data-testid="notebook-add"
            onClick={() => {
              setAddError(null);
              setAdding(true);
            }}
          >
            Add a note
          </button>
        ) : null}
      </div>

      {load.kind === 'ready' ? <NotebookStudy boatId={boatId} onDone={refresh} /> : null}

      {adding ? (
        <AddNoteForm
          equipment={equipment}
          busy={busyId !== null}
          error={addError}
          onSave={(input) => void onAdd(input)}
          onCancel={() => {
            setAdding(false);
            setAddError(null);
          }}
        />
      ) : null}
      {load.kind === 'loading' ? <p className="muted">Loading notes…</p> : null}
      {load.kind === 'error' ? (
        <ul className="form-errors">
          <li>Couldn’t load what Neo knows. Check the connection and try again.</li>
        </ul>
      ) : null}
      {load.kind === 'ready' && shown.length === 0 && !adding ? (
        <div className="empty-card" data-testid="notebook-empty">
          <p>Neo hasn’t noted anything about {boatName} yet.</p>
          <p className="muted">
            As it learns how the boat is set up, from its readings or from what you tell it, the
            notes show up here. You can add one yourself too.
          </p>
        </div>
      ) : null}

      {topics.map((topic) => (
        <section
          key={topic.area}
          className="notebook-topic"
          aria-labelledby={`notebook-${topic.area}`}
          data-testid={`notebook-topic-${topic.area}`}
        >
          <h3 id={`notebook-${topic.area}`} className="tb-section-label">
            {topic.heading}
          </h3>
          {topic.loose.length > 0 ? (
            <ul className="tb-list notebook-list">{topic.loose.map(row)}</ul>
          ) : null}
          {topic.equipment.map((group) => (
            <div
              key={group.instance}
              className="notebook-equipment"
              data-testid={`notebook-equipment-${group.instance}`}
            >
              <h4 className="notebook-equipment__label">{group.label}</h4>
              <ul className="tb-list notebook-list">{group.notes.map(row)}</ul>
            </div>
          ))}
        </section>
      ))}

      {outOfDate.length > 0 ? (
        <details className="notebook-outdated" data-testid="notebook-out-of-date">
          <summary>
            <ChevronRightIcon />
            Out of date <span className="notebook-outdated__count">{outOfDate.length}</span>
          </summary>
          <p className="muted notebook-outdated__help">
            Neo doesn’t use these. Confirm or correct one to bring it back, or forget it.
          </p>
          <ul className="tb-list notebook-list notebook-list--muted">{outOfDate.map(row)}</ul>
        </details>
      ) : null}
    </section>
  );
}
